import React, { useEffect, useReducer } from "react";
import clsx from "clsx";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import _ from "lodash";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";

import getBeepIds, { getSentBeeps } from "../UserBoards/getBeepIds";
import { getUnique } from "../tools";
import { AnalyserProvider } from "./analyserContext";
import moodboardReducer, { initialBoard } from "./moodboardReducer";
import analyserReducer, { initialAnalyser } from "./analyserReducer";
import useBeeps from "../UserTimeline/useBeeps";
import importAnswers from "../UserTimeline/ROM/importAnswers";
import TimeRangeSelector from "./TimeRangeSelector";
import TimeViewSelector from "./TimeViewSelector";
import useTimeline from "../UserTimeline/useTimeline";
import CustomDrawer from "../Components/CustomDrawer";
import Title from "./Title";
import CustomList from "./CustomList";
import useLocalMoodboard from "../UserBoards/useLocalMoodboard";
import BackButton from "../Navbar/BackButton";
import BoardList from "./BoardList";
import getBoard, { compareLabels } from "./moodboardModel";
import Disclaimer from "./Disclaimer";
import useClasses from "../useClasses";

const drawerWidth = 300;
const drawerMoodboardWidth = 300;

const styles = (theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    overflowX: "hidden",
  },
  drawerPaper: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    marginLeft: 80,
    width: drawerWidth,
  },
  drawerContainer: {
    overflow: "auto",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: drawerMoodboardWidth,
    marginLeft: drawerWidth,
  },
  contentShiftLeft: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  contentShiftRight: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  drawerMoodboard: {
    width: drawerMoodboardWidth,
    flexShrink: 0,
  },
  drawerMoodboardPaper: {
    width: drawerMoodboardWidth,
  },
});

export default function Analyser(props) {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  // local state
  const [moodboard, moodboardDispatch] = useReducer(
    moodboardReducer,
    initialBoard
  ); // global board settings
  //console.log("moodboard");
  //console.log(moodboard);
  const [analyser, analyserDispatch] = useReducer(
    analyserReducer,
    initialAnalyser
  );

  // server state
  const { localMoodboard } = useLocalMoodboard();
  const { timeline } = useTimeline(); // array of objects with client's timeStamps and beepIds
  const filteredTimeline = timeline?.events
    ?.filter(
      ({ time, itemId }) =>
        time >= moodboard.timeRange[0] &&
        time <= moodboard.timeRange[1] &&
        itemId !== null &&
        itemId?.[0] !== "!"
    )
    .sort((a, b) => b.time - a.time); // timeline should be between the defined timeRange and the itemId should not be null or start with !
  const itemIds = getUnique(filteredTimeline, "itemId").map(
    ({ itemId, title, type }) => ({
      id: itemId,
      title,
      type,
    })
  ); // array of surveys between 2 timePoints (sorted from recent to old)

  // BEEPS
  const beepIds = getBeepIds({ timeline, moodboard });
  const sentBeeps = getSentBeeps({ timeline, moodboard });
  const { beeps } = useBeeps(
    beepIds,
    timeline?.events && localMoodboard && JSON.stringify(moodboard)
  ); // fetch beeps by id
  const rom = beeps ? importAnswers({ ...beeps, sentBeeps }) : undefined; // import (visualized) answers from beeps
  //console.log(rom);
  const quantitativeLabels =
    rom?.table
      .filter(({ isQuantitative }) => isQuantitative)
      .flatMap(({ id, label, question, type, labelProps }) => ({
        id,
        title: label,
        question,
        type,
        labelProps,
      })) ?? []; // quantitative labels
  const qualitativeLabels =
    rom?.table
      .filter(({ isQuantitative }) => !isQuantitative)
      .flatMap(({ id, label, question, type, labelProps }) => ({
        id,
        title: label,
        question,
        type,
        labelProps,
      })) ?? []; // qualitative labels

  const quantitativeLabelsChecked = quantitativeLabels.filter(
    ({ labelProps }) =>
      moodboard.labelProps.some((prop) => compareLabels(prop, labelProps))
  ); // checked quantitative labels
  const qualitativeLabelsChecked = qualitativeLabels.filter(({ labelProps }) =>
    moodboard.labelProps.some((prop) => compareLabels(prop, labelProps))
  ); // checked qualitative labels

  // constructor at startup to create moodboards
  useEffect(() => {
    const state = props.location.state;
    if (state?.from === "timeline") {
      // open the analyser from timeline to explore
      moodboardDispatch({
        type: "UPDATE_MOODBOARD",
        payload: {
          itemIds: [state.itemId],
          labelProps: state.labelProps,
        },
      });
      analyserDispatch({ type: "SET_EXPLORE" });
    } else if (state?.from === "moodboards") {
      // open the analyser from moodboards to edit
      moodboardDispatch({
        type: "RESTORE_MOODBOARD",
        payload: state.moodboard,
      });
      analyserDispatch({ type: "SET_EDIT" });
    } else {
      // create a moodboard from scratch
      moodboardDispatch({
        type: "UPDATE_MOODBOARD",
        payload: { itemIds: [], labels: [], labelProps: [] },
      });
      analyserDispatch({ type: "SET_EXPLORE" });
    }
  }, []);

  return (
    <React.Fragment>
      <Disclaimer />
      <AnalyserProvider
        value={{
          moodboard,
          moodboardDispatch,
          analyser,
          analyserDispatch,
        }}
      >
        <CustomDrawer
          width={300}
          open={analyser.openSurveysAndLabels}
          content={
            <div>
              <Title />
              <CustomList
                initList={itemIds}
                title={t("moodboards.surveys")}
                type="itemId"
                handleClick
                selected={moodboard.itemIds}
              />
              <Divider />
              <CustomList
                initList={quantitativeLabels}
                title={t("moodboards.quantitative")}
                type="quantitative"
                selected={quantitativeLabelsChecked.map(({ id }) => id)}
              />
              <Divider />
              <CustomList
                initList={qualitativeLabels}
                title={t("moodboards.qualitative")}
                type="qualitative"
                selected={qualitativeLabelsChecked.map(({ id }) => id)}
              />
              <Divider />
              <div style={{ padding: 16, textAlign: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => moodboardDispatch({ type: "CLEAR_LABELS" })}
                >
                  {t("moodboards.buttons.clear")}
                </Button>
              </div>
            </div>
          }
        />
        <div
          key={timeline?.events}
          className={clsx(classes.content, {
            [classes.contentShiftRight]: !analyser.openMoodboards,
            [classes.contentShiftLeft]: !analyser.openSurveysAndLabels,
          })}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <BackButton />
              {!analyser.openSurveysAndLabels ? (
                <div>
                  <Button
                    variant="outlined"
                    onClick={() => analyserDispatch({ type: "TOGGLE_SURVEYS" })}
                  >
                    {t("moodboards.buttons.show")}
                  </Button>
                </div>
              ) : null}
            </div>
            <TimeViewSelector />
          </div>
          <TimeRangeSelector />
          <div style={{ marginTop: 16 }}></div>
          <BoardList
            mode={analyser.mode}
            moodboard={moodboard}
            quantitativeLabelsChecked={quantitativeLabelsChecked}
            qualitativeLabelsChecked={qualitativeLabelsChecked}
            rom={rom}
          />
        </div>
      </AnalyserProvider>
      <Box padding={8} />
    </React.Fragment>
  );
}
